// src/components/admin/Sidebar.jsx
import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import SignOutButton from "../ui/signOutButton";
import { useAuth } from "../../auth/AuthContext";

const baseLink =
  "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors";
const getLinkClass = ({ isActive }) =>
  isActive
    ? `${baseLink} bg-[var(--brand-soft)] text-[var(--ink)] font-semibold`
    : `${baseLink} text-slate-600 hover:bg-slate-100`;

/* =====================================================================
   🔑 NORMALIZADOR DE ROL
   - Toma user.rol | user.role | user.tipoUsuario (en ese orden)
   - Quita acentos y espacios/guiones/underscores
   - Convierte a minúsculas
   EJ: "Admin Tec" / "adminTec" -> "admintec"
===================================================================== */
function getCanonicalRole(user) {
  const raw = user?.rol ?? user?.role ?? user?.tipoUsuario ?? "";
  const s = String(raw)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[\s\-_]+/g, "");
  return s; // p.ej. "admin general" -> "admingeneral"
}

/* =====================================================================
   🧭 MAPA DE PERMISOS (CLAVES CANÓNICAS)
   - CLAVE: rol ya canonicalizado (ver getCanonicalRole)
   - VALOR: array de secciones habilitadas
   - Secciones posibles: "dashboard", "orders", "routes", "inventory",
     "users", "settings"
   👉 PARA CAMBIAR QUÉ VE CADA ROL, EDITA ESTE BLOQUE.
===================================================================== */
const PERMISSIONS = {
  admingeneral: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  admintec: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  admincomercial: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  bodeguero: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  serviciocliente: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  despacho: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  vendedor: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
  mensajero: ["dashboard", "orders", "routes", "inventory", "users", "settings"], // solo rutas
  // (opcional) rol genérico "admin" por si algún usuario trae eso:
  admin: ["dashboard", "orders", "routes", "inventory", "users", "settings"],
};

export default function Sidebar() {
  const { user } = useAuth();

  // Rol canonicalizado (coincide con las claves de PERMISSIONS)
  const roleKey = useMemo(() => getCanonicalRole(user), [user]);

  // Permisos según rol (con fallback); no ocultamos por usuario
  const allowed = useMemo(() => {
    const list = PERMISSIONS[roleKey];
    return Array.isArray(list) ? list : ["dashboard"]; // fallback seguro
  }, [roleKey]);

  const can = (section) => allowed.includes(section);

  // Dashboard visible si está permitido por rol
  const canSeeDashboard = useMemo(() => allowed.includes("dashboard"), [allowed]);

  return (
    <aside className="shrink-0">
      <style>{`
        :root{
          --brand:#244A9B;
          --brand-soft:#9EC2FF;
          --ink:#0F1D43;
        }
      `}</style>

      <div className="h-screen sticky top-0 flex flex-col border-r border-slate-200 w-64 md:w-72">
        {/* Header */}
        <div className="px-4 py-5 border-b border-slate-200 flex">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Promequi" className="h-10 w-auto" />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {/* ================== General ================== */}
          {can("dashboard") && canSeeDashboard && (
            <div>
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                General
              </div>
              <ul className="space-y-1">
                <li>
                  <NavLink to="/admin" end className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M3 10.5L12 4l9 6.5V20a2 2 0 0 1-2 2h-4.5a.5.5 0 0 1-.5-.5V15a2 2 0 0 0-2-2h0a2 2 0 0 0-2 2v6.5a.5.5 0 0 1-.5.5H5a2 2 0 0 1-2-2v-9.5z" />
                      </svg>
                    </span>
                    Dashboard
                  </NavLink>
                </li>
              </ul>
            </div>
          )}

          {/* ================== Órdenes ================== */}
          {can("orders") && (
            <div>
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Órdenes
              </div>
              <ul className="space-y-1">
                <li>
                  <NavLink to="/admin/ordenes-web" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 3a9 9 0 100 18 9 9 0 000-18z" />
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M2.5 12h19M12 2.5v19M7 12a16 16 0 003 7.5M17 12A16 16 0 0114 4.5" />
                      </svg>
                    </span>
                    Órdenes
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/ordenes-web/historial" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 8v5l3 3M12 3a9 9 0 100 18 9 9 0 000-18z" />
                      </svg>
                    </span>
                    Historial
                  </NavLink>
                </li>
              </ul>
            </div>
          )}

          {/* ================== Logística (Rutas) ================== */}
          {can("routes") && (
            <div>
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Logística
              </div>
              <ul className="space-y-1">
                <li>
                  <NavLink to="/admin/rutas" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M6 6h.01M18 18h.01M7 7c0 5 5 5 10 10M17 17l-4-1" />
                      </svg>
                    </span>
                    Rutas
                  </NavLink>
                </li>
              </ul>
            </div>
          )}

          {/* ================== Inventario ================== */}
          {can("inventory") && (
            <div>
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Inventario
              </div>
              <ul className="space-y-1">
                <li>
                  <NavLink to="/admin/productos" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M21 16V8a2 2 0 0 0-1.2-1.8l-6-3a2 2 0 0 0-1.8 0l-6 3A 2 2 0 0 0 4 8v8a2 2 0 0 0 1.2 1.8l6 3a2 2 0 0 0 1.6 0l6-3A2 2 0 0 0 21 16z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.3 7.3L12 12l8.7-4.7" />
                      </svg>
                    </span>
                    Productos
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/nuevo-producto" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                    Nuevo producto
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/cupones" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                    Cupones
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/inventario/historial" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 22a10 10 0 110-20 10 10 0 010 20z" />
                      </svg>
                    </span>
                    Historial (Cupones y Ofertas)
                  </NavLink>
                </li>
              </ul>
            </div>
          )}

          {/* ================== Usuarios ================== */}
          {can("users") && (
            <div>
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Usuarios
              </div>
              <ul className="space-y-1">
                <li>
                  <NavLink to="/admin/usuarios" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M16 14a4 4 0 10-8 0v1a4 4 0 100 8h8a4 4 0 100-8v-1z" />
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                      </svg>
                    </span>
                    Usuarios
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/nuevo-usuario" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M15 12a4 4 0 11-8 0 4 4 0 018 0zM19 8v6m3-3h-6" />
                      </svg>
                    </span>
                    Nuevo usuario
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/solicitudes-usuarios" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M16 14a4 4 0 10-8 0v1a4 4 0 100 8h8a4 4 0 100-8v-1z" />
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                      </svg>
                    </span>
                    Solicitudes de usuario
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/ascensos" className={getLinkClass}>
                    <span className="inline-block w-5 text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M16 14a4 4 0 10-8 0v1a4 4 0 100 8h8a4 4 0 100-8v-1z" />
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                      </svg>
                    </span>
                    Solicitudes Distribuidor
                  </NavLink>
                </li>
              </ul>
            </div>
          )}
        </nav>

        {/* ================== Footer: Configuraciones + Salir ================== */}
        {can("settings") ? (
          <div className="px-3 py-4 border-t border-slate-200 space-y-2">
            <NavLink to="/admin/configuraciones" className={getLinkClass}>
              <span className="inline-block w-5 text-slate-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M10.5 4.5l3 0M4.5 13.5l0-3m15 3l0-3M12 8.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7zM5 19l2-1m10 1l2-1M5 5l2 1m10-1l2 1" />
                </svg>
              </span>
              Configuraciones
            </NavLink>

            <div className="pt-2">
              <SignOutButton />
            </div>
          </div>
        ) : (
          <div className="px-3 py-4 border-t border-slate-200">
            <SignOutButton />
          </div>
        )}
      </div>
    </aside>
  );
}
